





































































































import { Component } from "vue-property-decorator";
import MiraDetailCardComponent from "@/components/mira/MiraDetailCard.vue";
import MiraFactsComponent from "@/components/mira/MiraFacts.vue";
import MiraKpiTileComponent from "@/components/mira/MiraKpiTile.vue";
import MiraKpiTileGridComponent from "@/components/mira/MiraKpiTileGrid.vue";
import MiraComparisonCardComponent from "@/components/mira/MiraComparisonCard.vue";
import MiraScreenBase from "@/components/mira/MiraScreenBase";
import MiraScreenHeaderComponent from "@/components/mira/MiraScreenHeader.vue";
import MiraStackedBarComponent from "@/components/mira/MiraStackedBar.vue";
import MiraTrendCardComponent from "@/components/mira/MiraTrendCard.vue";
import MiraStackedTrendCardComponent from "@/components/mira/MiraStackedTrendCard.vue";
import MiraUnlockListComponent from "@/components/mira/MiraUnlockList.vue";
import { MiraSection } from "@/data/models/Mira";
import { MiraSectionUnlocks, collectUnlocks } from "@/utils/MiraDataQuality";
import { MiraSegment, MiraTile, caveatText, publishedMetric } from "@/utils/MiraDataQualityView";
import { MiraFact } from "@/utils/MiraDryingView";
import {
  PROCESS_TRANSITION_GROUPS,
  buildProcessTiles,
  buildRouteSegments,
  processTrendFormatter,
  routeFacts,
} from "@/utils/MiraProcessFlowView";

/**
 * Mira Prozess: the transitions along a project's path (Hans, Track #2610040, P4: one tile per pair of
 * Zeitleiste steps, start to start, grouped by path) and the recording source as a card. The Projektablauf
 * card (PHA-011) is not shown since 2026-10-13: IRES Cloud still computes it until Hans has seen the
 * transitions, and its helpers stay in `MiraProcessFlowView.ts` for that reason.
 *
 * Built like the other Mira screens (`MiraScreenBase`). Phasendauer, Wartezeit and Reihenfolge der
 * Projektschritte were removed on 2026-10-05 (Hans, #2610009); the Stehende Projekte "Stand heute" card
 * (PHA-008) was retired on 2026-10-13 (#2610040, "Mira is a look back"). See
 * `docs/analytics/mira-office-screens.md`.
 */
@Component({
  components: {
    "mira-detail-card": MiraDetailCardComponent,
    "mira-facts": MiraFactsComponent,
    "mira-kpi-tile": MiraKpiTileComponent,
    "mira-kpi-tile-grid": MiraKpiTileGridComponent,
    "mira-screen-header": MiraScreenHeaderComponent,
    "mira-stacked-bar": MiraStackedBarComponent,
    "mira-comparison-card": MiraComparisonCardComponent,
    "mira-trend-card": MiraTrendCardComponent,
    "mira-stacked-trend-card": MiraStackedTrendCardComponent,
    "mira-unlock-list": MiraUnlockListComponent,
  },
})
export default class MiraProcessFlowComponent extends MiraScreenBase {
  get section(): MiraSection {
    return MiraSection.PROCESS_FLOW;
  }

  get titleKey(): string {
    return "mira.processFlow.title";
  }

  get tiles(): MiraTile[] {
    return buildProcessTiles(this.period, this.comparison, this.viewContext);
  }

  get routeSegments(): MiraSegment[] {
    return buildRouteSegments(this.period, this.viewContext);
  }

  get routeFacts(): MiraFact[] {
    return routeFacts(this.period, this.viewContext);
  }

  /**
   * The period tiles (with the group master's member suffix) split into the path groups, in group order;
   * a group with no published transition is left out, heading and all.
   */
  get tileGroups(): Array<{ key: string; tiles: MiraTile[] }> {
    const byId = new Map(this.periodTiles.map((tile) => [tile.id, tile]));
    return PROCESS_TRANSITION_GROUPS
      .map((group) => ({
        key: group.key,
        tiles: group.tiles.map(({ id }) => byId.get(id)).filter((tile): tile is MiraTile => !!tile),
      }))
      .filter((group) => group.tiles.length > 0);
  }

  /** "Recorded only since 2025", the caveat the figure is published with. */
  get routeCaveat(): string | null {
    return caveatText(publishedMetric(this.period, "MIRA-PHA-010"), this.viewContext);
  }

  /**
   * Only this section's figures waiting on data: the other sections have their own screens. Computed
   * here rather than through `super`, which vue-class-component's computed getters do not support.
   */
  get unlocks(): MiraSectionUnlocks[] {
    return collectUnlocks(this.sections, this.selectedPeriod).filter((group) => group.section === MiraSection.PROCESS_FLOW);
  }

  trendFormatter(metricId: string): (value: number) => string {
    return processTrendFormatter(metricId, this.viewContext, this.period);
  }
}
